import React from 'react';

import { Box, Button, Group, Popover, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { IconFilter, IconSearch, IconUser, IconX } from '@tabler/icons-react';
import type { AthleteKiloClassWithAthleteAndWaitlistOrder } from 'types/generated/models/AthleteKiloClassWithAthleteAndWaitlistOrder';

export type AthleteStatusFilter = 'all' | 'completed' | 'pending';

interface AthletesFilterMenuProps {
  status: AthleteStatusFilter;
  onStatusChange: (s: AthleteStatusFilter) => void;
  search: string;
  onSearchChange: (q: string) => void;
  visibleAthletes: AthleteKiloClassWithAthleteAndWaitlistOrder[];
}

const STATUS_OPTIONS: Array<{ value: AthleteStatusFilter; label: string }> = [
  { value: 'all', label: 'All Athletes' },
  { value: 'completed', label: 'Completed Only' },
  { value: 'pending', label: 'Pending Only' },
];

const AthletesFilterMenu = ({
  status,
  onStatusChange,
  search,
  onSearchChange,
  visibleAthletes,
}: AthletesFilterMenuProps): JSX.Element => {
  const hasActiveFilter = status !== 'all' || search.length > 0;

  return (
    <Popover position='bottom-end' shadow='md' width={320} withinPortal>
      <Popover.Target>
        <Button size='xs' variant='default' leftIcon={<IconFilter size={14} />}>
          All Athletes
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack spacing='md'>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Filter by Status
            </Text>
            {STATUS_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                size='sm'
                variant={status === opt.value ? 'filled' : 'subtle'}
                color={status === opt.value ? 'blue' : 'gray'}
                fullWidth
                styles={{ inner: { justifyContent: 'flex-start' } }}
                onClick={() => onStatusChange(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Stack>

          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Search Athlete
            </Text>
            <TextInput
              placeholder='Search by name...'
              icon={<IconSearch size={14} />}
              value={search}
              onChange={e => onSearchChange(e.currentTarget.value)}
            />
          </Stack>

          <ScrollArea h={200} type='auto'>
            {visibleAthletes.length === 0 ? (
              <Text size='sm' color='dimmed' py='xs' ta='center'>
                No athletes match.
              </Text>
            ) : (
              <Stack spacing={0}>
                {visibleAthletes.map(a => (
                  <Group
                    key={a.athleteKiloClassId}
                    spacing='xs'
                    py='xs'
                    px='xs'
                    noWrap
                    onClick={() => onSearchChange(a.athlete.fullName)}
                    sx={theme => ({
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.colors.gray[1]}`,
                      '&:hover': { backgroundColor: theme.colors.gray[0] },
                    })}
                  >
                    <Box
                      sx={theme => ({
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: theme.colors.gray[1],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      })}
                    >
                      <IconUser size={14} color='#868e96' />
                    </Box>
                    <Text size='sm'>{a.athlete.fullName}</Text>
                  </Group>
                ))}
              </Stack>
            )}
          </ScrollArea>

          {hasActiveFilter && (
            <Button
              variant='default'
              fullWidth
              leftIcon={<IconX size={14} />}
              onClick={() => {
                onStatusChange('all');
                onSearchChange('');
              }}
            >
              Clear All Filters
            </Button>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default AthletesFilterMenu;
